/// <amd-module name="Repos/Views/CreatePullRequest/Components/CreatePullRequestOverviewTab" />
import { MarkdownPreviewWithAutocomplete, MarkdownTextFieldWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import * as React from "react";
import { GitPullRequest } from "Repos/Common/Contracts/Git";
import { IPullRequestMarkdownToolbarContext } from "Repos/Common/Contracts/Markdown";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { tryAttachingPastedImage, wrapWithAttachmentDropTarget } from "Repos/PullRequest/Attachments/Attachment";
import { getSuggestedTagsService } from "Repos/PullRequestCommon/SuggestedTagsService";
import { getPullRequestDescriptionService, maxDescriptionLength } from "Repos/PullRequestCommon/PullRequestDescriptionService";
import * as Resources from "Repos/Views/CreatePullRequest/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { shortcutCommands } from "VSS/Features/MarkdownEditor/Commands/Shortcut";
import { toolbarCommands } from "VSS/Features/MarkdownEditor/Commands/Toolbar";
import { MarkdownToolbar } from "VSS/Features/MarkdownEditor/Components";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IRegionComponentProperties, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { IButtonProps } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { FormItem, FormItemContext } from "VSSUI/FormItem";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { SplitButton } from "VSSUI/SplitButton";
import { TagPicker } from "VSSUI/TagPicker";
import { TextField } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";
import { ICreatePullRequestViewService, IPullRequestCreateInfo } from "../CreatePullRequestViewService";

export interface IOverviewTabContentProps {
    createInfo: IPullRequestCreateInfo;
}

type PullRequestCreateListener = (pullRequest: GitPullRequest) => Promise<void> | void;
interface ICreatePullRequestOverviewExtensionContext {
    addPullRequestCreateListener: (listener: PullRequestCreateListener) => void;
    repository: {
        id: string;
        name: string;
    };
    sourceVersion: string;
    targetVersion: string;
}

const showRequiredReviewersSettingsKey = "Repos.CreatePullRequest.ShowRequiredReviewers";

export class OverviewTabContent extends VssComponent<IOverviewTabContentProps> {
    private descriptionRef = React.createRef<MarkdownTextFieldWithAutocomplete>();
    private inputElement = React.createRef<HTMLTextAreaElement & HTMLInputElement>();
    private pickerProvider: IPeoplePickerProvider;
    private pullRequestCreateListeners: PullRequestCreateListener[] = [];
    private showRequiredReviewers = new ObservableValue(tryGetLocalStorageValue(showRequiredReviewersSettingsKey) === "true");
    private warningDismissed = new ObservableValue(false);
    private errorMessage = new ObservableValue("");
    private markdownError = new ObservableValue<Error | null>(null);

    constructor(props: IOverviewTabContentProps, context: IVssComponentContext) {
        super(props, context);

        this.pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: ["user", "group"]
        });
    }

    public render() {
        const { createInfo } = this.props;
        const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");
        const prDescriptionService = getPullRequestDescriptionService(this.context.pageContext);
        const tagsService = getSuggestedTagsService(this.context.pageContext);

        const templateMenuItems: IMenuItem[] = createInfo.templates.map(t => {
            return {
                id: t.path,
                text: t.name,
                secondaryText: t.isDefault ? Resources.DefaultTemplateLabel : undefined
            };
        });

        const descriptionHeaderItems: IHeaderCommandBarItem[] = [];
        if (createInfo.commits.length > 1) {
            descriptionHeaderItems.push({
                iconProps: { iconName: "Paste" },
                id: "add-commit-messages",
                important: true,
                onActivate: () => createPRService.addCommitMessagesToDescription(),
                subtle: true,
                text: Resources.AddCommitMessages
            });
        }
        if (templateMenuItems.length) {
            descriptionHeaderItems.push({
                id: "apply-templates-menu",
                important: true,
                subMenuProps: {
                    id: "add-templates-submenu",
                    items: templateMenuItems,
                    onActivate: menuItem => createPRService.addTemplateToDescription(menuItem.id)
                },
                subtle: true,
                text: Resources.AddTemplate
            });
        }

        // Can be removed when FF will be removed.
        let isShowWarning: ((value: string) => boolean) | undefined = (value) => prDescriptionService.isDescriptionActualLengthValid(value);
        let localMaxDescriptionLength: number | undefined = maxDescriptionLength;
        let localWarningText: string | undefined = Resources.DescriptionTooLongWarning;

        if (!isFeatureFlagEnabled(this.context.pageContext, "SourceControl.GitPullRequests.DescriptionLimit", false)) {
            localMaxDescriptionLength = undefined;
            isShowWarning = undefined;
            localWarningText = undefined;
        }

        const descriptionTextField = (
            <MarkdownTextFieldWithAutocomplete
                ariaLabel={Resources.Description}
                error={this.markdownError}
                maxLength={localMaxDescriptionLength}
                inputClassName="repos-pr-create-description"
                messageText={Resources.AttachmentSupportMessage}
                onChange={description => createPRService.setDescription(description)}
                onError={error => (this.markdownError.value = error)}
                onPaste={(e, textSource) => tryAttachingPastedImage(e, this.context.pageContext, pullRequestMarkdownContext, textSource)}
                placeholder={Resources.CreateDescriptionPlaceholder}
                ref={this.descriptionRef}
                shortcutCommands={shortcutCommands}
                inputElement={this.inputElement}
                value={createPRService.getDescription()}
                showInfoLinkForWorkItemLinking
                warningText={localWarningText}
                isShowWarning={isShowWarning}
                showCharacterCounter={isFeatureFlagEnabled(this.context.pageContext, "SourceControl.GitPullRequests.DescriptionLimit", false)}
            />
        );

        const pullRequestMarkdownContext = {
            projectId: createPRService.getSourceTargetInfo().value.tgtRepository.project.id,
            pullRequestId: 0,
            repositoryId: createPRService.getSourceTargetInfo().value.tgtRepository.id
        };

        return (
            <Observer
                errorMessage={this.errorMessage}
                showRequiredReviewers={this.showRequiredReviewers}
                title={createPRService.getTitle()}
                warningDismissed={this.warningDismissed}
            >
                {(props: { errorMessage: string; showRequiredReviewers: boolean; title: string; warningDismissed: boolean }) => {
                    return (
                        <div className="repos-pr-create-form page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                            {props.errorMessage && (
                                <MessageCard onDismiss={() => (this.errorMessage.value = "")} severity={MessageCardSeverity.Error}>
                                    {props.errorMessage}
                                </MessageCard>
                            )}
                            {!props.warningDismissed && createInfo.commits.length >= createInfo.maxNumberOfCommits && (
                                <MessageCard onDismiss={() => (this.warningDismissed.value = true)} severity={MessageCardSeverity.Warning}>
                                    {format(Resources.LargePullRequestWarningFormat, createInfo.maxNumberOfCommits, createInfo.sourceTargetInfo.tgtBranch)}
                                </MessageCard>
                            )}
                            <Card contentProps={{ className: "rhythm-vertical-16 flex-column" }}>
                                <div className="flex-grow flex-column rhythm-vertical-16 scroll-hidden">
                                    <FormItem
                                        label={Resources.Title}
                                        error={!props.title}
                                        message={props.title ? undefined : Resources.TitleRequired}
                                    >
                                        <TextField
                                            autoFocus={true}
                                            className="repos-pr-create-form-title"
                                            onChange={(e, title) => createPRService.setTitle(title)}
                                            placeholder={Resources.CreateTitlePlaceholder}
                                            value={props.title}
                                        />
                                    </FormItem>
                                    <div className="flex-column rhythm-vertical-8">
                                        <FormItemContext.Provider value={{ ariaLabelledById: "pr-description-label" }}>
                                            <div className="flex-row flex-center rhythm-horizontal-8">
                                                <label className="body-m" id={getSafeId("pr-description-label")}>
                                                    {Resources.Description}
                                                </label>
                                                <div className="flex-grow" />
                                                <HeaderCommandBar items={descriptionHeaderItems} />
                                            </div>
                                            {wrapWithAttachmentDropTarget(
                                                this.context.pageContext,
                                                pullRequestMarkdownContext,
                                                descriptionTextField,
                                                this.descriptionRef
                                            )}
                                            <MarkdownToolbar
                                                commands={toolbarCommands}
                                                contributionId="ms.vss-code-web.pr-create-markdown-commands"
                                                contributionContext={
                                                    {
                                                        projectId: createInfo.sourceTargetInfo.tgtRepository.project.id,
                                                        pullRequestId: 0,
                                                        repositoryId: createInfo.sourceTargetInfo.tgtRepository.id
                                                    } as IPullRequestMarkdownToolbarContext
                                                }
                                                getTextSource={() => this.descriptionRef.current}
                                            />
                                            <MarkdownPreviewWithAutocomplete
                                                refreshThrottleMs={1000}
                                                value={createPRService.getDescription()}
                                                options={{ breaks: true, html: true }}
                                            />
                                        </FormItemContext.Provider>
                                    </div>
                                    <div className="flex-column rhythm-vertical-8">
                                        <FormItemContext.Provider value={{ ariaLabelledById: "pr-optional-reviewers-label" }}>
                                            <div className="flex-row flex-center rhythm-horizontal-8">
                                                <label className="body-m" id={getSafeId("pr-optional-reviewers-label")}>
                                                    {props.showRequiredReviewers ? Resources.OptionalReviewers : Resources.Reviewers}
                                                </label>
                                                <Observer requiredReviewers={createPRService.getRequiredReviewers()}>
                                                    {(requiredReviewerProps: { requiredReviewers: IIdentity[] }) => {
                                                        const reviewerHeaderItems: IHeaderCommandBarItem[] = [];
                                                        if (requiredReviewerProps.requiredReviewers.length === 0 || !props.showRequiredReviewers) {
                                                            reviewerHeaderItems.push({
                                                                id: "toggle-required-reviewers",
                                                                important: true,
                                                                onActivate: () => {
                                                                    const newValue = !props.showRequiredReviewers;
                                                                    this.showRequiredReviewers.value = newValue;
                                                                    trySetLocalStorageValue(
                                                                        showRequiredReviewersSettingsKey,
                                                                        newValue ? "true" : undefined
                                                                    );
                                                                },
                                                                subtle: true,
                                                                text: props.showRequiredReviewers
                                                                    ? Resources.HideRequiredReviewers
                                                                    : Resources.AddRequiredReviewers
                                                            });
                                                        }
                                                        return (
                                                            <>
                                                                <span className="flex-grow" />
                                                                <HeaderCommandBar items={reviewerHeaderItems} />
                                                            </>
                                                        );
                                                    }}
                                                </Observer>
                                            </div>
                                            <IdentityPicker
                                                placeholderText={
                                                    props.showRequiredReviewers
                                                        ? Resources.OptionalReviewersPlaceholder
                                                        : Resources.ReviewersPlaceholder
                                                }
                                                selectedIdentities={createPRService.getOptionalReviewers()}
                                                onResolveEntity={(email, identity) => createPRService.resolveReviewerEmail(email, identity)}
                                                onIdentityAdded={r => createPRService.addOptionalReviewer(r)}
                                                onIdentityRemoved={r => createPRService.removeOptionalReviewers([r])}
                                                onIdentitiesRemoved={r => createPRService.removeOptionalReviewers(r)}
                                                pickerProvider={this.pickerProvider}
                                            />
                                        </FormItemContext.Provider>
                                    </div>
                                    {props.showRequiredReviewers && (
                                        <div className="flex-column rhythm-vertical-8">
                                            <FormItemContext.Provider value={{ ariaLabelledById: "pr-required-reviewers-label" }}>
                                                <div className="flex-row flex-center rhythm-horizontal-8">
                                                    <label className="body-m" id={getSafeId("pr-required-reviewers-label")}>
                                                        {Resources.RequiredReviewers}
                                                    </label>
                                                </div>
                                                <IdentityPicker
                                                    placeholderText={Resources.RequiredReviewersPlaceholder}
                                                    selectedIdentities={createPRService.getRequiredReviewers()}
                                                    onResolveEntity={(email, identity) => createPRService.resolveReviewerEmail(email, identity)}
                                                    onIdentityAdded={r => createPRService.addRequiredReviewer(r)}
                                                    onIdentityRemoved={r => createPRService.removeRequiredReviewers([r])}
                                                    onIdentitiesRemoved={r => createPRService.removeRequiredReviewers(r)}
                                                    pickerProvider={this.pickerProvider}
                                                />
                                            </FormItemContext.Provider>
                                        </div>
                                    )}
                                    <WrappedComponent<IRegionComponentProperties>
                                        dependencies={["ms.vss-code-web.pr-create-overview-extensions"]}
                                        wrappedType="ms.vss-web.region"
                                        wrappedProps={{
                                            childComponentProperties: {
                                                createPullRequestContext: {
                                                    addPullRequestCreateListener: (listener: PullRequestCreateListener) =>
                                                        this.pullRequestCreateListeners.push(listener),
                                                    commits: createInfo.commits,
                                                    pullRequestCreationId: createPRService.getPullRequestCreationId(),
                                                    repository: {
                                                        id: createInfo.sourceTargetInfo.tgtRepository.id,
                                                        name: createInfo.sourceTargetInfo.tgtRepository.name
                                                    },
                                                    sourceVersion: createInfo.sourceVersionString,
                                                    targetVersion: createInfo.targetVersionString
                                                } as ICreatePullRequestOverviewExtensionContext
                                            },
                                            className: "rhythm-vertical-16",
                                            componentRegion: "createPullRequestOverviewExtensions"
                                        }}
                                    />
                                    <div className="flex-column rhythm-vertical-8">
                                        <FormItemContext.Provider value={{ ariaLabelledById: "pr-tags-label" }}>
                                            <div className="flex-row flex-center rhythm-horizontal-8">
                                                <label className="body-m" id={getSafeId("pr-tags-label")}>
                                                    {Resources.Tags}
                                                </label>
                                            </div>

                                            <TagPicker<string>
                                                areTagsEqual={(a, b) => a.toLowerCase() === b.toLowerCase()}
                                                createDefaultItem={this.createDefaultTag}
                                                convertItemToPill={s => {
                                                    return { content: s };
                                                }}
                                                noResultsFoundText={""}
                                                onSearchChanged={searchText => tagsService.updateSuggestedTags(searchText)}
                                                onSearchChangedDebounceWait={0}
                                                onTagAdded={tagName => createPRService.addTag(tagName)}
                                                onTagRemoved={tagName => createPRService.removeTag(tagName)}
                                                renderSuggestionItem={suggestion => <div className="body-m">{suggestion.item}</div>}
                                                selectedTags={createPRService.getTags()}
                                                suggestions={tagsService.getSuggestedTags(createInfo.sourceTargetInfo.tgtRepository)}
                                                suggestionsLoading={tagsService.getSuggestedTagsLoading()}
                                            />
                                        </FormItemContext.Provider>
                                    </div>
                                    <div className="flex-row justify-end">
                                        <SplitButton
                                            buttonProps={this.getDefaultButtonAction(createInfo.draftIsDefault || props.title.toLocaleLowerCase().startsWith("[wip]"))}
                                            disabled={!props.title}
                                            menuButtonProps={{
                                                contextualMenuProps: {
                                                    menuProps: {
                                                        id: "1",
                                                        items: [
                                                            {
                                                                id: "create",
                                                                className: "create-pr-btn",
                                                                onActivate: () => {
                                                                    this.createPullRequest(false);
                                                                },
                                                                text: Resources.Create
                                                            },
                                                            {
                                                                iconProps: { iconName: "EditNote" },
                                                                id: "create-draft",
                                                                onActivate: () => {
                                                                    this.createPullRequest(true);
                                                                },
                                                                text: Resources.CreateAsDraft
                                                            }
                                                        ]
                                                    }
                                                }
                                            }}
                                            primary={true}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private createDefaultTag: (searchText: string) => string | undefined = (searchText: string) => {
        const tagsService = getSuggestedTagsService(this.context.pageContext);
        return searchText === "" || tagsService.getUsedTags().has(searchText.toLowerCase()) ? undefined : searchText;
    };

    private createPullRequest = async (draft: boolean) => {
        const createPRService = this.context.pageContext.getService<ICreatePullRequestViewService>("ICreatePullRequestViewService");

        let createdPullRequest: GitPullRequest;
        try {
            createdPullRequest = await createPRService.createPullRequest(draft);
        } catch (ex) {
            this.errorMessage.value = format(Resources.CreatePullRequestErrorFormat, ex.message);
            return;
        }

        for (const listener of this.pullRequestCreateListeners) {
            const result = listener(createdPullRequest);
            if (result && typeof result.then === "function") {
                try {
                    await result;
                } catch (ex) {
                    // Show error in ReposMessages
                    const repoMessagesService = this.context.pageContext.getService<IReposMessagesService>("IReposMessagesService");
                    repoMessagesService.showMessage({ renderMessage: () => ex.message, severity: MessageCardSeverity.Warning }, true);
                }
            }
        }

        const routingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const href = routingService.getPullRequestUrl(
            createdPullRequest.repository.name,
            createdPullRequest.repository.project.name,
            createdPullRequest.pullRequestId
        );

        FastPageSwitch(this.context.pageContext, href, true);
    };

    private getDefaultButtonAction = (isDraft?: boolean) : IButtonProps => {
        const createButton: IButtonProps = {
            onClick: () => {
                this.createPullRequest(false);
            },
            text: Resources.Create
        };

        const createDraftButton: IButtonProps = {
            iconProps: { iconName: "EditNote" },
            onClick: () => {
                this.createPullRequest(true);
            },
            text: Resources.CreateAsDraft
        };

        if (isDraft) {
            return createDraftButton;
        }

        return createButton;
    }
}
